/**
 * Vulnerabilities → Overview.
 *
 * The job of this panel is to get the admin packaging through ALICE. So it
 * answers, in order: how exposed are we, what should I do first, what does
 * doing it buy me, and what is ALICE already handling. Everything comes
 * from one /api/cve/dashboard call.
 *
 * What it deliberately does NOT show: a severity donut of families and a
 * CVE-age histogram. Both describe how scary the estate is without
 * pointing at an action, and the old "riskiest families" ranking used a
 * different score from the Fix Queue, so the two tabs disagreed. The
 * "Start here" list IS the top of the Fix Queue — same score, same bands,
 * same manual decisions.
 *
 * Scope of every number here: apps ALICE has MATCHED to a CVE product.
 * ALICE is not a scanner. It matches installed apps to published CVEs and
 * gets packaging started before the security team asks. So the copy says
 * "matched", never "everything".
 *
 * The posture table sits above the queue on purpose: it lists EVERY mapped
 * family — live CVE or not — with its ALICE posture. Reactive work is the
 * queue; proactive work is getting that whole table to "auto-resolving",
 * so the day a CVE lands nothing needs a human.
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import {
  Flame, Users, ShieldCheck, HelpCircle, PackagePlus, Zap, Package, ArrowRight,
  Clock, ListOrdered, ListChecks,
} from "lucide-react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import RemediationPostureWidget, { PosturePill } from "./RemediationPostureWidget";

const CARD_BG =
  "bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] " +
  "border border-[rgba(229,239,250,0.1)] rounded-xl";
const BTN_GHOST =
  "bg-[rgba(229,239,250,0.08)] border-[rgba(229,239,250,0.2)] text-white hover:bg-white/10";

const SEVERITY_BADGE = {
  CRITICAL: "bg-red-600 text-white",
  HIGH: "bg-orange-600 text-white",
  MEDIUM: "bg-yellow-600 text-white",
  LOW: "bg-blue-600 text-white",
};

const BAND_CHIP = {
  do_now:          { label: "Do now",         cls: "bg-red-600 text-white" },
  one_click:       { label: "One click",      cls: "bg-emerald-600 text-white" },
  needs_packaging: { label: "Package",        cls: "bg-amber-500 text-black" },
  needs_packaging_no_cve: { label: "No CVE · package", cls: "bg-sky-700 text-white" },
  handled:         { label: "ALICE handles",  cls: "bg-white/15 text-white" },
};

// Mirrors the Fix Queue's primary action. Only "create package" is acted on
// here; everything else opens the queue where the real controls live.
const ACTION_META = {
  enable_auto_update: { label: "Enable auto-update", icon: Zap,         tone: "bg-emerald-600 hover:bg-emerald-500 text-white" },
  create_package:     { label: "Create package",     icon: PackagePlus, tone: "bg-cyan-600 hover:bg-cyan-500 text-white" },
  update_package:     { label: "Update package",     icon: Package,     tone: "bg-cyan-600 hover:bg-cyan-500 text-white" },
  verify_rollout:     { label: "View package",       icon: ShieldCheck, tone: BTN_GHOST },
  map_cpe:            { label: "Map CPE",            icon: HelpCircle,  tone: "bg-amber-600 hover:bg-amber-500 text-white" },
};

const fmt = (n) => (n || 0).toLocaleString();

// ---------- KPI ---------------------------------------------------------------

const Kpi = ({ icon: Icon, label, value, sub, tone = "default", onClick, testId }) => {
  const toneClasses = {
    default: "text-white",
    kev:     "text-red-300",
    warning: "text-orange-300",
    ok:      "text-emerald-300",
  }[tone] || "text-white";
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      className={`${CARD_BG} p-4 flex items-start gap-3 text-left w-full ${onClick ? "hover:bg-white/[0.08] transition focus:outline-none focus:ring-2 focus:ring-cyan-500" : ""}`}
      onClick={onClick}
      data-testid={testId}
    >
      <div className={`shrink-0 rounded-lg p-2 bg-white/5 ${toneClasses}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wide text-white/60">{label}</div>
        <div className={`text-2xl font-semibold leading-tight ${toneClasses}`}>{value}</div>
        {sub && <div className="text-xs text-white/50 mt-0.5">{sub}</div>}
      </div>
    </Tag>
  );
};

// ---------- Start here: top of the Fix Queue -----------------------------------

const StartHere = ({ rows, payoff, pctBase, onSelectFamily, onOpenQueue, onJumpToUnmapped }) => {
  const navigate = useNavigate();

  const act = (r) => {
    if (r.next_action === "create_package") {
      navigate("/catalog", {
        state: r.winget_id
          ? { autoPackage: true, wingetId: r.winget_id, fromDiscover: true, discoverFamilyName: r.family_name }
          : { customUpload: true, appName: r.app_name || r.family_name, familyName: r.family_name, fromDiscover: true, targetVersion: r.worst_fix_version || null },
      });
      return;
    }
    if (r.next_action === "map_cpe") { onJumpToUnmapped?.(); return; }
    onOpenQueue?.();
  };

  return (
    <div className={`${CARD_BG} p-5 flex flex-col`} data-testid="widget-start-here">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <div className="text-sm font-semibold text-white flex items-center gap-2">
            <ListOrdered className="w-4 h-4 text-cyan-300" /> Start here
          </div>
          <div className="text-xs text-white/60 mt-0.5">
            Top of the Fix Queue — same score, same order. Work it top to bottom, ahead of the security team.
          </div>
        </div>
        <Button size="sm" variant="outline" className={BTN_GHOST} onClick={onOpenQueue} data-testid="start-here-open-queue">
          Open Fix Queue <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>

      {payoff?.families > 0 && (
        <div className="rounded-lg bg-cyan-500/10 border border-cyan-400/30 px-3 py-2 text-sm text-white/90 mt-2 mb-3" data-testid="start-here-payoff">
          Packaging these <b>{payoff.families}</b> covers <b>{fmt(payoff.devices_cleared)}</b> of the <b>{fmt(pctBase)}</b> devices ALICE has matched to a CVE
          {payoff.kev_families > 0 && <>, including <b className="text-red-300">{payoff.kev_families}</b> under active exploitation</>}
          {payoff.one_click_families > 0 && <>; <b className="text-emerald-300">{payoff.one_click_families}</b> {payoff.one_click_families === 1 ? "is" : "are"} a single toggle</>}.
          <span className="text-white/55"> Matched apps only — the “Not yet matched” tile above is what ALICE cannot see.</span>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="py-8 text-center text-sm text-white/60">
          <ShieldCheck className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
          Nothing needs a human right now.
        </div>
      ) : (
        <ol className="divide-y divide-white/[0.06]" data-testid="start-here-list">
          {rows.map((r, i) => {
            const band = BAND_CHIP[r.band] || BAND_CHIP.needs_packaging;
            const am = ACTION_META[r.next_action] || ACTION_META.create_package;
            const AIcon = am.icon;
            const sev = (r.highest_severity || "").toUpperCase();
            return (
              <li key={r.family_name} className="flex items-center gap-3 py-2" data-testid={`start-here-row-${i + 1}`}>
                <span className="w-6 text-right font-mono text-sm text-white/50 tabular-nums shrink-0">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => onSelectFamily?.(r.family_name)}
                      className="text-white font-medium hover:text-cyan-300 text-left truncate max-w-[28ch]"
                      title="Show this family's versions and CVEs below"
                    >
                      {r.app_name || r.family_name}
                    </button>
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${band.cls}`}>{band.label}</span>
                    {r.has_kev && (
                      <Badge className="bg-rose-700 text-white text-[10px] inline-flex items-center gap-1" title="Actively exploited (CISA KEV)">
                        <Flame className="w-3 h-3" /> KEV
                      </Badge>
                    )}
                    {sev && <Badge className={`${SEVERITY_BADGE[sev] || "bg-gray-600 text-white"} text-[10px]`}>{sev}</Badge>}
                    {r.is_breach && (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-orange-600 text-white" title="Past the Cyber Essentials 14-day window">
                        {r.days_overdue}d overdue
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-white/55 flex items-center gap-2 flex-wrap">
                    <span><b className="text-white/80 tabular-nums">{fmt(r.device_count)}</b> devices</span>
                    <span>·</span>
                    {r.band === "needs_packaging_no_cve"
                      ? <span>no CVE today · {r.version_count || 1} version{r.version_count === 1 ? "" : "s"} in estate</span>
                      : <span>{fmt(r.cve_count)} CVE{r.cve_count === 1 ? "" : "s"}</span>}
                    <span>·</span>
                    <PosturePill posture={r.posture} size="xs" />
                    {r.winget_id && <span className="text-cyan-300/80" title="A Winget package matches this family — ALICE can build and keep it updated without an installer upload">· automatable</span>}
                  </div>
                </div>
                <Button size="sm" onClick={() => act(r)} className={`${am.tone} shrink-0`} data-testid={`start-here-action-${i + 1}`}>
                  <AIcon className="w-4 h-4 mr-1" /> {am.label}
                </Button>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

// ---------- Every mapped family, with its ALICE posture ------------------------
// The proactive view. A mapped family with no CVE today still has a
// posture, and the goal is every row reading "auto-resolving".

const POSTURE_FILTERS = [
  ["all", "All"],
  ["unmanaged", "Unmanaged"],
  ["stale_package", "Stale package"],
  ["auto_resolving", "Auto-resolving"],
];
const MAPPED_RENDER_CAP = 50;

const MappedFamiliesTable = ({ rows, postureRows, onRefresh, onSelectFamily }) => {
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(null);

  const counts = Object.fromEntries((postureRows || []).map((r) => [r.state, r.families || 0]));
  const total = rows.length;
  const filtered = filter === "all" ? rows : rows.filter((r) => r.posture === filter);
  const shown = filtered.slice(0, MAPPED_RENDER_CAP);
  const autoPct = total > 0 ? Math.round(((counts.auto_resolving || 0) / total) * 100) : 0;

  const act = async (r) => {
    switch (r.next_action) {
      case "enable_auto_update": {
        if (!r.package_id) { navigate("/packages"); return; }
        setBusy(r.family_name);
        try {
          const token = await getAccessToken();
          await axios.post(`${API}/packages/${encodeURIComponent(r.package_id)}/toggle-auto-update`, null, {
            headers: { Authorization: `Bearer ${token}` }, params: { enabled: true },
          });
          toast.success(`Auto-update enabled for ${r.family_name}`);
          onRefresh?.();
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Could not enable auto-update");
        } finally {
          setBusy(null);
        }
        return;
      }
      case "create_package":
        navigate("/catalog", {
          state: r.winget_id
            ? { autoPackage: true, wingetId: r.winget_id, fromDiscover: true, discoverFamilyName: r.family_name }
            : { customUpload: true, appName: r.family_name, familyName: r.family_name, fromDiscover: true },
        });
        return;
      default:
        navigate("/packages");
    }
  };

  return (
    <div className={`${CARD_BG} p-5 flex flex-col h-full`} data-testid="widget-mapped-families">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-white flex items-center gap-2">
            <ListChecks className="w-4 h-4 text-cyan-300" /> Every mapped family and its ALICE posture
          </div>
          <div className="text-xs text-white/60 mt-0.5">
            Goal: all <b className="text-white/80">{fmt(total)}</b> families auto-resolving — currently{" "}
            <b className={autoPct >= 75 ? "text-emerald-300" : autoPct >= 40 ? "text-orange-300" : "text-red-300"}>{autoPct}%</b>.
            Package them before a CVE lands, not after.
          </div>
        </div>
        <div className="inline-flex rounded-lg bg-white/[0.06] p-0.5 border border-white/[0.08]" role="tablist">
          {POSTURE_FILTERS.map(([id, label]) => (
            <button
              key={id}
              onClick={() => setFilter(id)}
              className={`text-[11px] px-2 py-1 rounded-md transition ${filter === id ? "bg-cyan-600/70 text-white" : "text-white/70 hover:text-white"}`}
              data-testid={`mapped-filter-${id}`}
            >
              {label}{id !== "all" && <span className="ml-1 text-white/50">{counts[id] || 0}</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-3 max-h-[420px] overflow-auto rounded-lg border border-[rgba(229,239,250,0.1)]">
        <Table className="table-fixed">
          <TableHeader>
            <TableRow className="border-b-[rgba(229,239,250,0.1)] hover:bg-transparent">
              <TableHead className="text-white/90 font-semibold">Family</TableHead>
              <TableHead className="text-white/90 font-semibold w-[190px]">Today</TableHead>
              <TableHead className="text-white/90 font-semibold w-[80px] text-right">Installs</TableHead>
              <TableHead className="text-white/90 font-semibold w-[150px]">Posture</TableHead>
              <TableHead className="text-white/90 font-semibold w-[170px] text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-white/55 text-sm">
                {total === 0 ? "No families are mapped to a CVE product yet." : "Nothing in this posture."}
              </TableCell></TableRow>
            )}
            {shown.map((r) => {
              const am = ACTION_META[r.next_action] || ACTION_META.create_package;
              const AIcon = am.icon;
              const sev = (r.highest_severity || "").toUpperCase();
              return (
                <TableRow key={r.family_name} className="border-b-[rgba(229,239,250,0.06)] hover:bg-white/[0.04]" data-testid={`mapped-row-${r.family_name}`}>
                  <TableCell>
                    <button onClick={() => onSelectFamily?.(r.family_name)} className="text-white font-medium hover:text-cyan-300 text-left break-words" title="Show this family's versions and CVEs below">
                      {r.family_name}
                    </button>
                    <div className="font-mono text-[10px] text-white/45 break-all">{r.cpe}</div>
                  </TableCell>
                  <TableCell>
                    {r.is_vulnerable ? (
                      <div className="flex items-center gap-1 flex-wrap">
                        {sev && <Badge className={`${SEVERITY_BADGE[sev] || "bg-gray-600 text-white"} text-[10px]`}>{sev}</Badge>}
                        {r.has_kev && <Badge className="bg-rose-700 text-white text-[10px] inline-flex items-center gap-1"><Flame className="w-3 h-3" /> KEV</Badge>}
                        <span className="text-[11px] text-white/70 tabular-nums">{fmt(r.vulnerable_device_count)} devices</span>
                      </div>
                    ) : (
                      <span className="text-[11px] text-white/45">No CVE matched today</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-white/85 tabular-nums text-sm">{fmt(r.installs)}</TableCell>
                  <TableCell><PosturePill posture={r.posture} /></TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" onClick={() => act(r)} disabled={busy === r.family_name} className={am.tone} data-testid={`mapped-action-${r.family_name}`}>
                      <AIcon className={`w-4 h-4 mr-1 ${busy === r.family_name ? "animate-pulse" : ""}`} /> {am.label}
                    </Button>
                    {r.winget_id && r.next_action === "create_package" && (
                      <div className="text-[10px] text-cyan-300/80 mt-0.5">automatable</div>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {filtered.length > shown.length && (
        <div className="text-[11px] text-white/50 mt-2">
          Showing {shown.length} of {fmt(filtered.length)} — worst posture first, vulnerable first, biggest first.
        </div>
      )}
    </div>
  );
};

// ---------- Cyber Essentials clock ---------------------------------------------

const CeClock = ({ ce, onOpenBreaches }) => {
  const installs = ce?.total_installs || 0;
  const worst = ce?.worst_days_overdue || 0;
  const products = ce?.products_affected || 0;
  const tone = installs > 0 ? (worst >= 30 ? "text-red-300" : "text-orange-300") : "text-emerald-300";
  return (
    <div className={`${CARD_BG} p-5 flex flex-col lg:flex-row lg:items-center gap-4`} data-testid="widget-ce-clock">
      <div className="lg:w-[300px] shrink-0">
        <div className="text-sm font-semibold text-white flex items-center gap-2">
          <Clock className="w-4 h-4 text-cyan-300" /> Cyber Essentials 14-day clock
        </div>
        <div className="text-xs text-white/60 mt-0.5">High/Critical must be fixed within 14 days of a fix being available.</div>
        <div className="text-[11px] text-white/55 mt-2">
          {installs > 0
            ? "Every breach row is also in the Fix Queue's “Do now” band — packaging clears the clock."
            : "Nothing is past the 14-day window."}
        </div>
      </div>
      <div className="alice-kpi-row grid grid-cols-3 gap-2 flex-1 min-w-0">
        <div className="rounded-md bg-[rgba(229,239,250,0.05)] border border-[rgba(229,239,250,0.08)] px-3 py-2">
          <div className="text-[10px] uppercase tracking-wide text-white/55">In breach</div>
          <div className={`text-2xl font-semibold leading-tight tabular-nums ${tone}`}>{fmt(installs)}</div>
          <div className="text-[11px] text-white/50">installs · {products} product{products === 1 ? "" : "s"}</div>
        </div>
        <div className="rounded-md bg-[rgba(229,239,250,0.05)] border border-[rgba(229,239,250,0.08)] px-3 py-2">
          <div className="text-[10px] uppercase tracking-wide text-white/55">Critical / High</div>
          <div className="text-2xl font-semibold leading-tight tabular-nums text-white">
            <span className="text-red-300">{fmt(ce?.critical_installs)}</span>
            <span className="text-white/40 mx-1">/</span>
            <span className="text-orange-300">{fmt(ce?.high_installs)}</span>
          </div>
          <div className="text-[11px] text-white/50">installs past the window</div>
        </div>
        <div className="rounded-md bg-[rgba(229,239,250,0.05)] border border-[rgba(229,239,250,0.08)] px-3 py-2">
          <div className="text-[10px] uppercase tracking-wide text-white/55">Longest overdue</div>
          <div className={`text-2xl font-semibold leading-tight tabular-nums ${tone}`}>{worst ? `${worst}d` : "—"}</div>
          <div className="text-[11px] text-white/50">since first exposed</div>
        </div>
      </div>
      <Button size="sm" variant="outline" className={`${BTN_GHOST} shrink-0 self-start lg:self-center`} onClick={onOpenBreaches} data-testid="ce-clock-open">
        Evidence <ArrowRight className="w-4 h-4 ml-1" />
      </Button>
    </div>
  );
};

// ---------- Main --------------------------------------------------------------

const CVEDashboardWidgets = ({ data, loading, onSelectFamily, onJumpToUnmapped, onOpenQueue, onOpenBreaches, onRefresh }) => {
  const [postureMetric, setPostureMetric] = useState("families");

  if (loading && !data) {
    return <div className={`${CARD_BG} p-6 mb-6 text-white/60 text-sm`}>Loading overview…</div>;
  }
  if (!data) return null;

  const t = data.totals || {};
  const mapping = data.mapping_coverage || {};
  const ce = data.ce_breach_summary || {};
  const qs = data.fix_queue_summary || {};
  const bands = qs.bands || [];
  const bandActive = (id) => bands.find((b) => b.band === id) || {};
  const pctAtRisk = t.total_devices > 0 ? Math.round((t.devices_at_risk / t.total_devices) * 100) : 0;
  const postureRows = data.remediation_posture || [];
  const mappedTotal = postureRows.reduce((acc, r) => acc + (r.families || 0), 0);
  const autoFamilies = (postureRows.find((r) => r.state === "auto_resolving") || {}).families || 0;
  const autoFamPct = mappedTotal > 0 ? Math.round((autoFamilies / mappedTotal) * 100) : 0;
  // Reactive rows (bands 1-3) are the headline; the proactive no-CVE band
  // is shown alongside so the number is never mistaken for live exposure.
  const reactiveOpen = ["do_now", "one_click", "needs_packaging"].reduce((acc, b) => acc + (bandActive(b).active || 0), 0);
  const proactiveOpen = bandActive("needs_packaging_no_cve").active || 0;
  const packagingDevices = (bandActive("needs_packaging").devices || 0) + (bandActive("one_click").devices || 0);

  return (
    <div className="mb-6 space-y-4" data-testid="cve-dashboard">
      {/* Row 1 — exposure and the size of the job */}
      <div className="alice-kpi-row grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Kpi
          icon={Users}
          label="Devices with a matched CVE"
          value={fmt(t.devices_at_risk)}
          sub={`${pctAtRisk}% of ${fmt(t.total_devices)} devices · ${t.vulnerable_families || 0} matched families`}
          tone={pctAtRisk > 50 ? "kev" : pctAtRisk > 20 ? "warning" : "ok"}
          testId="kpi-devices-at-risk"
        />
        <Kpi
          icon={Flame}
          label="Under active exploitation"
          value={t.kev_family_count || 0}
          sub={t.kev_family_count > 0 ? `${fmt(t.kev_device_count)} devices on the CISA KEV list` : "No KEV-listed families detected"}
          tone={t.kev_family_count > 0 ? "kev" : "ok"}
          onClick={onOpenQueue}
          testId="kpi-kev"
        />
        <Kpi
          icon={ListOrdered}
          label="Waiting on you"
          value={fmt(reactiveOpen)}
          sub={reactiveOpen > 0
            ? `${fmt(packagingDevices)} devices cleared by packaging${proactiveOpen ? ` · +${fmt(proactiveOpen)} to package before a CVE lands` : ""}`
            : proactiveOpen ? `Nothing with a live CVE · ${fmt(proactiveOpen)} to package before a CVE lands` : "Nothing needs a human"}
          tone={reactiveOpen > 0 ? "warning" : "ok"}
          onClick={onOpenQueue}
          testId="kpi-waiting"
        />
        <Kpi
          icon={ShieldCheck}
          label="ALICE auto-resolving"
          value={`${autoFamPct}%`}
          sub={mappedTotal > 0
            ? `${fmt(autoFamilies)} of ${fmt(mappedTotal)} mapped families fix themselves on the next release — packaging moves this up`
            : "No families mapped to a CVE product yet"}
          tone={autoFamPct >= 75 ? "ok" : autoFamPct >= 40 ? "warning" : "kev"}
          testId="kpi-auto-resolving"
        />
        <Kpi
          icon={HelpCircle}
          label="Not yet matched"
          value={fmt(mapping.unmapped_families)}
          sub={mapping.unmapped_families > 0
            ? `families · ${fmt(mapping.unmapped_installs)} installs ALICE cannot see yet — click to map`
            : `Every family is linked to a CVE product`}
          tone={mapping.unmapped_families > 0 ? "warning" : "ok"}
          onClick={onJumpToUnmapped}
          testId="kpi-unmapped"
        />
      </div>

      {/* Row 2 — proactive: every mapped family's posture, goal = all auto-resolving */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-stretch">
        <div className="min-w-0">
          <RemediationPostureWidget
            data={data.remediation_posture}
            metric={postureMetric}
            onMetricChange={setPostureMetric}
          />
        </div>
        <div className="xl:col-span-2 min-w-0">
          <MappedFamiliesTable
            rows={data.mapped_families || []}
            postureRows={postureRows}
            onRefresh={onRefresh}
            onSelectFamily={onSelectFamily}
          />
        </div>
      </div>

      {/* Row 3 — reactive: what to do first. Full width; the clock sits
          beneath as a strip rather than beside as a short card. */}
      <StartHere
        rows={data.fix_queue_preview || []}
        payoff={qs.payoff_top10}
        pctBase={t.devices_at_risk || 0}
        onSelectFamily={onSelectFamily}
        onOpenQueue={onOpenQueue}
        onJumpToUnmapped={onJumpToUnmapped}
      />

      {/* Row 4 — the compliance clock, as a horizontal strip */}
      <CeClock ce={ce} onOpenBreaches={onOpenBreaches} />
    </div>
  );
};

export default CVEDashboardWidgets;
